import { useCallback, useEffect, useRef, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { renderPoster } from "@/lib/renderPoster";
import { api } from "@/lib/api";
import { Poster, PosterContent } from "@/components/PosterCanvas";
import { A4Preview } from "@/components/A4Preview";
import { UscControls } from "@/components/UscControls";
import { mapBlocks } from "@/components/SelectionPanel";
import { SendPosterButton } from "@/components/SendPosterButton";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle,
} from "@/components/ui/dialog";
import { toast } from "sonner";
import { ArrowLeft, Save, Loader2, ImagePlus, Trash2, Download, Bookmark, Lock, Unlock, Image as ImageIcon, Monitor, Printer, Undo2, Redo2 } from "lucide-react";
import { DropdownMenu, DropdownMenuTrigger, DropdownMenuContent, DropdownMenuItem } from "@/components/ui/dropdown-menu";

const A4_H = Math.round(1280 / Math.SQRT2);

const BG_PRESETS = [["", "origine"], ["/backgrounds/trails.jpg", "lumieres"], ["/backgrounds/smoke.jpg", "fumee"], ["/backgrounds/speed.jpg", "vitesse"], ["/backgrounds/shards.jpg", "eclats"]];

export default function PosterEditor() {
  const { id } = useParams();
  const navigate = useNavigate();
  const logoRef = useRef(null);
  const bgRef = useRef(null);
  const ovRef = useRef(null);
  const [uploadingOv, setUploadingOv] = useState(false);
  const [data, setRaw] = useState(null);
  const cur = useRef(null);
  const hist = useRef({ past: [], future: [], t: 0 });
  const [, setTick] = useState(0);
  const replace = useCallback((v) => {
    cur.current = typeof v === "function" ? v(cur.current) : v;
    setRaw(cur.current);
  }, []);
  const setData = useCallback((v) => {
    const prev = cur.current;
    const next = typeof v === "function" ? v(prev) : v;
    const h = hist.current;
    const now = Date.now();
    const onlySel = prev && next && Object.keys({ ...prev, ...next }).every((k) => k === "_sel" || prev[k] === next[k]);
    if (prev && next !== prev && !onlySel) {
      if (now - h.t > 700) { h.past.push(prev); if (h.past.length > 60) h.past.shift(); }
      h.t = now;
      h.future = [];
      setTick((n) => n + 1);
    }
    cur.current = next;
    setRaw(next);
  }, []);
  const step = useCallback((back) => {
    const h = hist.current;
    const from = back ? h.past : h.future;
    if (!from.length) return;
    (back ? h.future : h.past).push(cur.current);
    const sel = cur.current?._sel;
    cur.current = { ...from.pop(), _sel: sel };
    h.t = 0;
    setRaw(cur.current);
    setTick((n) => n + 1);
  }, []);
  useEffect(() => {
    const ARROWS = { ArrowLeft: [-1, 0], ArrowRight: [1, 0], ArrowUp: [0, -1], ArrowDown: [0, 1] };
    const onKey = (e) => {
      const sel = cur.current?._sel || [];
      if (e.key === "Escape" && sel.length && !/^(INPUT|TEXTAREA|SELECT)$/.test(e.target.tagName)) { setData((d) => ({ ...d, _sel: [] })); return; }
      if (ARROWS[e.key] && sel.length && !/^(INPUT|TEXTAREA|SELECT)$/.test(e.target.tagName) && !e.target.isContentEditable && !e.target.closest?.("[role='slider']")) {
        e.preventDefault();
        const [x, y] = ARROWS[e.key].map((v) => v * (e.shiftKey ? 10 : 1));
        setData((d) => mapBlocks(d, (rows) => rows.map((r) => (sel.includes(r.id) ? { ...r, dx: (r.dx || 0) + x, dy: (r.dy || 0) + y } : r))));
        return;
      }
      if (!(e.ctrlKey || e.metaKey) || e.key.toLowerCase() !== "z" && e.key.toLowerCase() !== "y") return;
      if (/^(INPUT|TEXTAREA|SELECT)$/.test(e.target.tagName) || e.target.isContentEditable) return;
      e.preventDefault();
      step(e.key.toLowerCase() === "z" && !e.shiftKey);
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [step, setData]);
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [uploadingBg, setUploadingBg] = useState(false);
  const [exporting, setExporting] = useState(false);
  const exportRef = useRef(null);
  const a4Ref = useRef(null);
  const [tplOpen, setTplOpen] = useState(false);
  const [templates, setTemplates] = useState(null);
  const [tplName, setTplName] = useState("");

  useEffect(() => {
    api
      .get(`/posters/${id}`)
      .then(async ({ data }) => {
        const teams = await api.get("/team-colors").then((r) => r.data.teams).catch(() => null);
        if (!teams) return replace(data);
        const own = ((data.band || {}).team_colors || []).filter((t) => !teams.some((g) => g.name.toLowerCase() === (t.name || "").toLowerCase()));
        replace({ ...data, band: { ...(data.band || {}), team_colors: [...teams, ...own] } });
      })
      .catch(() => {
        toast.error("Affiche introuvable");
        navigate("/posters");
      });
  }, [id, navigate, replace]);

  const save = async () => {
    setSaving(true);
    try {
      const payload = {
        name: data.name,
        title: data.title,
        club_name: data.club_name,
        logo_url: data.logo_url || "",
        team_name: data.team_name || "",
        highlight_team: data.highlight_team !== false,
        highlight_first_team: data.highlight_first_team !== false,
        show_scores: data.show_scores === true,
        color_primary: data.color_primary || "#182f5c",
        color_accent: data.color_accent || "#c6a15a",
        locked: data.locked === true,
        background_url: data.background_url || "",
        columns: data.columns,
        footer: data.footer,
        layout: data.layout || "standard",
        top: data.top || {},
        band: data.band || {},
        show_columns: data.show_columns !== false,
        show_logo: data.show_logo !== false,
        show_footer: data.show_footer !== false,
        show_title: data.show_title !== false,
        source_poster_id: data.source_poster_id || "",
        split_of: data.split_of || "",
        split_kind: data.split_kind || "",
        gold_lines: data.gold_lines || [],
        overlays: data.overlays || [],
      };
      const { data: fresh } = await api.put(`/posters/${id}`, payload);
      replace((d) => ({ ...fresh, _sel: d?._sel }));
      await saveSnapshot();
      toast.success("Enregistré (images TV et A4 mises à jour)");
      return true;
    } catch {
      toast.error("Échec de l'enregistrement");
      return false;
    } finally {
      setSaving(false);
    }
  };

  const uploadBackground = async (file) => {
    if (!file) return;
    setUploadingBg(true);
    try {
      const form = new FormData();
      form.append("file", file);
      const { data: res } = await api.post("/upload", form, { headers: { "Content-Type": "multipart/form-data" } });
      setData((d) => ({ ...d, background_url: res.url }));
      toast.success("Fond modifié");
    } catch {
      toast.error("Échec de l'envoi du fond");
    } finally {
      setUploadingBg(false);
      if (bgRef.current) bgRef.current.value = "";
    }
  };

  const uploadOverlay = async (file) => {
    if (!file) return;
    setUploadingOv(true);
    try {
      const form = new FormData();
      form.append("file", file);
      const { data: res } = await api.post("/upload", form, { headers: { "Content-Type": "multipart/form-data" } });
      const o = { id: Math.random().toString(36).slice(2, 10), url: res.url, x: 0.42, y: 0.35, w: 0.16 };
      setData((d) => ({ ...d, overlays: [...(d.overlays || []), o] }));
      toast.success("Image ajoutée : déplacez-la sur l'affiche, poignée orange pour la taille — pensez à enregistrer");
    } catch {
      toast.error("Échec de l'envoi de l'image");
    } finally {
      setUploadingOv(false);
      if (ovRef.current) ovRef.current.value = "";
    }
  };

  const uploadLogo = async (file) => {
    if (!file) return;
    setUploading(true);
    try {
      const form = new FormData();
      form.append("file", file);
      const { data: res } = await api.post("/upload", form, { headers: { "Content-Type": "multipart/form-data" } });
      setData((d) => ({ ...d, logo_url: res.url }));
      toast.success("Logo ajouté");
    } catch {
      toast.error("Échec de l'envoi du logo");
    } finally {
      setUploading(false);
      if (logoRef.current) logoRef.current.value = "";
    }
  };

  const fileBase = (data?.name || "affiche").replace(/[^a-z0-9]+/gi, "_");
  const render = async (scale, node = exportRef.current, height = 720) => {
    return renderPoster(node, { pixelRatio: scale, width: 1280, height, backgroundColor: "#0b1a3a", cacheBust: true });
  };

  const uploadRender = async (canvas, kind) => {
    const blob = await new Promise((resolve) => canvas.toBlob(resolve, "image/jpeg", 0.9));
    const form = new FormData();
    form.append("file", blob, `${kind}.jpg`);
    form.append("kind", kind);
    const { data: res } = await api.post(`/posters/${id}/snapshot`, form, { headers: { "Content-Type": "multipart/form-data" } });
    replace((d) => ({ ...d, ...res }));
  };

  const saveSnapshot = async () => {
    if (!exportRef.current) return;
    try {
      await uploadRender(await render(1.5), "tv");
      const isUsc = data.layout === "usc" && a4Ref.current;
      await uploadRender(await render(2.6, isUsc ? a4Ref.current : exportRef.current, isUsc ? A4_H : 720), "a4");
    } catch {
      toast.warning("Image pour TV ancienne non générée — réenregistrez l'affiche");
    }
  };

  const needsSnapshot = !!data && (!data.snapshot_url || !data.a4_url);
  useEffect(() => {
    if (!needsSnapshot) return;
    const t = setTimeout(saveSnapshot, 1500);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [needsSnapshot]);

  const runExport = async (fn) => {
    if (!exportRef.current) return;
    setExporting(true);
    try { await fn(); } catch { toast.error("Échec de l'export"); } finally { setExporting(false); }
  };

  const download = (canvas, name, type = "image/png", q) => {
    const link = document.createElement("a");
    link.download = name;
    link.href = canvas.toDataURL(type, q);
    link.click();
  };

  const exportPng = () => runExport(async () => {
    download(await render(2), `${fileBase}_HD.png`);
    toast.success("Image HD téléchargée (2560×1440)");
  });

  const exportScreen = () => runExport(async () => {
    download(await render(1.5), `${fileBase}_ecran_1920x1080.jpg`, "image/jpeg", 0.95);
    toast.success("Image écran téléchargée (1920×1080)");
  });

  const exportA4 = () => runExport(async () => {
    const isUsc = data.layout === "usc";
    download(await render(2.6, isUsc ? a4Ref.current : exportRef.current, isUsc ? A4_H : 720), `${fileBase}_A4.jpg`, "image/jpeg", 0.95);
    toast.success("Image A4 téléchargée");
  });

  const printA4 = () => runExport(async () => {
    const isUsc = data.layout === "usc";
    const url = (await render(2.6, isUsc ? a4Ref.current : exportRef.current, isUsc ? A4_H : 720)).toDataURL("image/jpeg", 0.95);
    const w = window.open("", "_blank");
    if (!w) return toast.error("Autorisez les fenêtres pop-up pour imprimer");
    const doc = w.document;
    doc.title = fileBase;
    const style = doc.createElement("style");
    style.textContent = `@page { size: A4 landscape; margin: 0; }
      html, body { margin: 0; height: 100%; background: #0b1a3a; -webkit-print-color-adjust: exact; print-color-adjust: exact; }
      body { display: flex; align-items: center; justify-content: center; }
      img { width: 297mm; height: 210mm; object-fit: ${isUsc ? "fill" : "contain"}; display: block; }`;
    doc.head.appendChild(style);
    const img = doc.createElement("img");
    img.onload = () => setTimeout(() => { w.focus(); w.print(); }, 300);
    img.src = url;
    doc.body.appendChild(img);
  });

  const openTemplates = async () => {
    setTplOpen(true);
    if (templates === null) {
      try { const { data: t } = await api.get("/poster-templates"); setTemplates(t); }
      catch { setTemplates([]); }
    }
  };

  const saveTemplate = async () => {
    const name = (tplName || data.club_name || "Mon modèle").trim();
    try {
      const { data: t } = await api.post("/poster-templates", {
        name, club_name: data.club_name, logo_url: data.logo_url || "", background_url: data.background_url || "",
        team_name: data.team_name, color_primary: data.color_primary, color_accent: data.color_accent,
      });
      setTemplates((list) => [t, ...(list || [])]);
      setTplName("");
      toast.success("Modèle enregistré");
    } catch { toast.error("Échec de l'enregistrement du modèle"); }
  };

  const applyTemplate = (t) => {
    setData((d) => ({
      ...d, club_name: t.club_name, logo_url: t.logo_url || "", background_url: t.background_url || "", team_name: t.team_name,
      color_primary: t.color_primary, color_accent: t.color_accent,
    }));
    setTplOpen(false);
    toast.success("Modèle appliqué");
  };

  const deleteTemplate = async (tid) => {
    try { await api.delete(`/poster-templates/${tid}`); setTemplates((l) => l.filter((x) => x.id !== tid)); }
    catch { toast.error("Échec de la suppression"); }
  };

  if (!data) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-background text-muted-foreground">
        <Loader2 className="h-6 w-6 animate-spin" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background">
      <div className="grain-overlay pointer-events-none fixed inset-0 opacity-60" />
      <header className="relative z-30 border-b border-border bg-background/80 backdrop-blur-xl md:sticky md:top-11">
        <div className="mx-auto flex max-w-[1700px] flex-wrap items-center justify-between gap-3 px-4 py-3 sm:px-6 lg:px-8">
          <div className="flex min-w-0 items-center gap-3">
            <Button variant="ghost" size="icon" onClick={() => navigate("/posters")} data-testid="back-button">
              <ArrowLeft className="h-5 w-5" />
            </Button>
            <Input
              value={data.name}
              onChange={(e) => setData({ ...data, name: e.target.value })}
              className="w-72 max-w-full border-transparent bg-transparent font-display text-lg font-bold focus-visible:border-input"
              data-testid="poster-name-input"
            />
          </div>
          <div className="flex flex-wrap items-center gap-2">
            <input ref={logoRef} type="file" accept="image/*" className="hidden" onChange={(e) => uploadLogo(e.target.files?.[0])} data-testid="logo-input" />
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button variant="secondary" disabled={exporting} data-testid="export-menu-button">
                  {exporting ? <Loader2 className="mr-1 h-4 w-4 animate-spin" /> : <Download className="mr-1 h-4 w-4" />}
                  Exporter
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end">
                <DropdownMenuItem onClick={exportScreen} data-testid="export-screen-button">
                  <Monitor className="mr-2 h-4 w-4" /> Image écran (1920×1080)
                </DropdownMenuItem>
                <DropdownMenuItem onClick={exportPng} data-testid="export-png-button">
                  <Download className="mr-2 h-4 w-4" /> PNG haute définition
                </DropdownMenuItem>
                <DropdownMenuItem onClick={exportA4} data-testid="export-a4-button">
                  <ImageIcon className="mr-2 h-4 w-4" /> Image A4 paysage (JPG)
                </DropdownMenuItem>
                <DropdownMenuItem onClick={printA4} data-testid="print-a4-button">
                  <Printer className="mr-2 h-4 w-4" /> Imprimer (A4 paysage)
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
            <div className="flex">
              <Button variant="secondary" size="icon" className="rounded-r-none" onClick={() => step(true)} disabled={!hist.current.past.length}
                title="Annuler (Ctrl+Z)" data-testid="undo-button"><Undo2 className="h-4 w-4" /></Button>
              <Button variant="secondary" size="icon" className="rounded-l-none border-l border-border" onClick={() => step(false)} disabled={!hist.current.future.length}
                title="Rétablir (Ctrl+Y)" data-testid="redo-button"><Redo2 className="h-4 w-4" /></Button>
            </div>
            <SendPosterButton poster={data} beforeSend={save} />
            <Button onClick={save} disabled={saving} data-testid="save-poster-button">
              {saving ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <Save className="mr-1 h-4 w-4" />}
              Enregistrer
            </Button>
          </div>
        </div>
      </header>

      <main className="relative z-10 mx-auto max-w-[1700px] px-4 py-6 sm:px-6 lg:px-8">
        <p className="mb-3 font-mono-label text-[10px] uppercase tracking-widest text-muted-foreground">
          Lignes : clic = sélectionner · Maj/Ctrl + clic = en ajouter · glisser = déplacer la sélection · double-clic = modifier le texte · Titres : un clic pour modifier
        </p>


        <div className="grid items-start gap-6 xl:grid-cols-[minmax(0,1fr)_380px]">
        <div className="min-w-0 space-y-4">
          <div className="overflow-hidden rounded-2xl border border-border shadow-2xl" data-testid="poster-editor-canvas">
            <Poster data={data} editable onChange={setData} className="aspect-video w-full" />
          </div>
          {data.layout === "usc" && <A4Preview data={data} height={A4_H} />}
        </div>
        <aside className="space-y-3 xl:sticky xl:top-28 xl:max-h-[calc(100vh-8rem)] xl:overflow-y-auto xl:pr-1" data-testid="poster-inspector">
        <div className="flex flex-col gap-3 rounded-xl border border-border bg-card p-4">
          <p className="font-display text-sm font-bold">Réglages</p>
          <div className="flex items-center gap-2">
            <Label htmlFor="team" className="text-xs">Mon équipe</Label>
            <Input id="team" value={data.team_name || ""} onChange={(e) => setData({ ...data, team_name: e.target.value })}
              className="h-8 w-28" placeholder="USC" data-testid="team-name-input" />
          </div>

          <div className="flex items-center gap-2">
            <Label className="text-xs">Fond</Label>
            <input ref={bgRef} type="file" accept="image/*" className="hidden" onChange={(e) => uploadBackground(e.target.files?.[0])} data-testid="background-input" />
            <Button variant="secondary" size="sm" onClick={() => bgRef.current?.click()} disabled={uploadingBg} data-testid="upload-background-button">
              {uploadingBg ? <Loader2 className="mr-1 h-4 w-4 animate-spin" /> : <ImageIcon className="mr-1 h-4 w-4" />}
              Changer le fond
            </Button>
            {data.background_url && (
              <Button variant="ghost" size="sm" className="text-muted-foreground" onClick={() => setData({ ...data, background_url: "" })} data-testid="reset-background-button">
                Fond par défaut
              </Button>
            )}
          </div>
          <div className="grid grid-cols-5 gap-1.5" data-testid="background-presets">
            {BG_PRESETS.map(([url, label]) => (
              <button key={label} onClick={() => setData({ ...data, background_url: url })} title={label} data-testid={`background-preset-${label}`}
                className={`aspect-video overflow-hidden rounded-md border-2 transition-transform hover:scale-105 ${(data.background_url || "") === url ? "border-primary" : "border-transparent"}`}>
                <img src={url || "/poster-bg.jpg"} alt={label} className="h-full w-full object-cover" />
              </button>
            ))}
          </div>
          {data.layout === "usc" && (
            <div className="flex items-center gap-2">
              <Label className="text-xs">Images</Label>
              <input ref={ovRef} type="file" accept="image/*" className="hidden" onChange={(e) => uploadOverlay(e.target.files?.[0])} data-testid="overlay-input" />
              <Button variant="secondary" size="sm" onClick={() => ovRef.current?.click()} disabled={uploadingOv} data-testid="add-overlay-button">
                {uploadingOv ? <Loader2 className="mr-1 h-4 w-4 animate-spin" /> : <ImagePlus className="mr-1 h-4 w-4" />}
                Ajouter une image
              </Button>
              {(data.overlays || []).length > 0 && <span className="text-xs text-muted-foreground" data-testid="overlay-count">{data.overlays.length} sur l'affiche</span>}
            </div>
          )}
          {data.layout === "usc" && <UscControls data={data} setData={setData} />}
        </div>
        </aside>
        </div>

        {/* hidden full-size node used for PNG export */}
        <div style={{ position: "fixed", left: -20000, top: 0, width: 1280, pointerEvents: "none", zIndex: -1 }}>
          <div ref={exportRef} style={{ width: 1280, height: 720 }}>
            <PosterContent data={data} editable={false} />
          </div>
          {data.layout === "usc" && (
            <div ref={a4Ref} style={{ width: 1280, height: A4_H }}>
              <PosterContent data={data} editable={false} height={A4_H} />
            </div>
          )}
        </div>

        <Dialog open={tplOpen} onOpenChange={setTplOpen}>
          <DialogContent data-testid="templates-dialog">
            <DialogHeader><DialogTitle className="font-display">Modèles de style</DialogTitle></DialogHeader>
            <div className="space-y-4">
              <p className="text-xs text-muted-foreground">
                Un modèle mémorise vos couleurs, votre logo, le nom du club et de l'équipe pour les réutiliser sur n'importe quelle affiche.
              </p>
              <div className="flex items-end gap-2">
                <div className="flex-1 space-y-1.5">
                  <Label className="text-xs">Enregistrer le style actuel</Label>
                  <Input value={tplName} onChange={(e) => setTplName(e.target.value)}
                    placeholder="Nom du modèle (ex. USC Jappes)" data-testid="template-name-input" />
                </div>
                <Button onClick={saveTemplate} data-testid="save-template-button">
                  <Bookmark className="mr-1 h-4 w-4" /> Enregistrer
                </Button>
              </div>
              <div className="max-h-[50vh] space-y-2 overflow-y-auto">
                {templates === null ? (
                  <div className="flex justify-center py-6 text-muted-foreground"><Loader2 className="h-5 w-5 animate-spin" /></div>
                ) : templates.length === 0 ? (
                  <p className="py-4 text-center text-sm text-muted-foreground">Aucun modèle enregistré.</p>
                ) : (
                  templates.map((t) => (
                    <div key={t.id} className="flex items-center gap-3 rounded-lg border border-border p-2" data-testid={`template-row-${t.id}`}>
                      <div className="flex gap-1">
                        <span className="h-6 w-6 rounded" style={{ background: t.color_primary }} />
                        <span className="h-6 w-6 rounded" style={{ background: t.color_accent }} />
                      </div>
                      <span className="flex-1 truncate text-sm font-medium">{t.name}</span>
                      <Button size="sm" onClick={() => applyTemplate(t)} data-testid={`apply-template-${t.id}`}>Appliquer</Button>
                      <Button size="icon" variant="ghost" className="text-destructive hover:text-destructive"
                        onClick={() => deleteTemplate(t.id)} data-testid={`delete-template-${t.id}`}>
                        <Trash2 className="h-4 w-4" />
                      </Button>
                    </div>
                  ))
                )}
              </div>
            </div>
          </DialogContent>
        </Dialog>
      </main>
    </div>
  );
}
